/**
 * Fase 6.5B — forced Terms acceptance + Privacy notice acknowledgement.
 *
 * Rendered by RequireAuth when `legalRequired === true`. Fetches the
 * canonical versions and per-user status on mount so it only renders the
 * checkbox(es) that the server actually still needs. Terms-only bump → 1
 * Terms box. Privacy-only bump → 1 Privacy box. First-time login → both.
 *
 * The user's Privacy acknowledgement is deliberately NOT worded as consent
 * — the Privacy Notice is a transparency document under GDPR Art. 13/14,
 * not a lawful basis. See dict.js for the exact copy.
 */
import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";

export default function LegalAcceptance() {
  const { t } = useI18n();
  const { refreshUser, logout } = useAuth();
  const [current, setCurrent] = useState(null);
  const [status, setStatus] = useState(null);
  const [terms, setTerms] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchLegal = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        api.get("/legal/current"),
        api.get("/legal/status"),
      ]);
      setCurrent(c.data);
      setStatus(s.data);
      setTerms(false);
      setPrivacy(false);
    } catch (e) {
      // Intentionally do NOT depend on `t` here — otherwise the callback
      // (and its useEffect) would re-fire whenever the i18n context ref
      // changes. Fallback text is only used when the fetch itself fails;
      // in that case a stale English string is fine.
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLegal(); }, [fetchLegal]);

  if (loading || !current || !status) {
    return (
      <div
        className="min-h-screen grid place-items-center text-[13px] text-[hsl(var(--text-muted))]"
        data-testid="legal-loading"
      >
        {t("legal_loading")}
      </div>
    );
  }

  const termsReq = !!status.terms_required;
  const privacyReq = !!status.privacy_notice_required;

  // Choose an on-brand, honest title based on what is required.
  const isFirstTime = !status.accepted_terms_version && !status.seen_privacy_notice_version;
  let title;
  if (isFirstTime) {
    title = t("legal_title_first");
  } else if (termsReq && !privacyReq) {
    title = t("legal_title_updated");
  } else if (privacyReq && !termsReq) {
    title = t("legal_title_privacy_updated");
  } else {
    title = t("legal_title_updated");
  }

  const canSubmit =
    !busy &&
    (!termsReq || terms) &&
    (!privacyReq || privacy);

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true); setError("");
    try {
      await api.post("/legal/accept", {
        terms_version: current.terms_version,
        privacy_notice_version: current.privacy_notice_version,
        // Client only sends true for the flag it was actually asked about.
        terms_accepted: termsReq ? terms : false,
        privacy_notice_acknowledged: privacyReq ? privacy : false,
      });
      toast.success(t("legal_accept_success"));
      await refreshUser();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.code === "legal_version_outdated") {
        setError(t("legal_version_outdated"));
        await fetchLegal();
      } else {
        setError(formatApiErrorDetail(detail) || t("legal_error"));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[hsl(var(--bg))] grid place-items-center px-4 py-10"
      data-testid="legal-acceptance-screen"
    >
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="card p-6 md:p-8 max-w-lg w-full"
      >
        <div className="eyebrow mb-2">Rostr</div>
        <h1
          className="font-display text-[28px] tracking-tight text-[hsl(var(--text-strong))] mb-3"
          data-testid="legal-title"
        >
          {title}
        </h1>
        <p className="text-[14px] text-[hsl(var(--text-muted))] leading-relaxed mb-4">
          {t("legal_intro")}
        </p>
        <div
          className="flex flex-col gap-1 text-[12px] text-[hsl(var(--text-muted))] mb-5 min-w-0"
          data-testid="legal-versions"
        >
          <span data-testid="legal-terms-version" className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span>{t("legal_current_terms_version")}:</span>
            <code className="text-[hsl(var(--text))] break-all">{current.terms_version}</code>
          </span>
          <span data-testid="legal-privacy-version" className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span>{t("legal_current_privacy_version")}:</span>
            <code className="text-[hsl(var(--text))] break-all">{current.privacy_notice_version}</code>
          </span>
        </div>

        <div className="flex flex-col gap-3 mb-5">
          {termsReq ? (
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={terms}
                onChange={(e) => setTerms(e.target.checked)}
                className="mt-1"
                data-testid="legal-terms-checkbox"
              />
              <span className="text-[14px] text-[hsl(var(--text))]">
                {t("legal_terms_checkbox_prefix")}{" "}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noreferrer"
                  className="underline"
                  data-testid="legal-terms-link"
                >
                  {t("legal_terms_link")}
                </a>
                {t("legal_terms_checkbox_suffix")}
              </span>
            </label>
          ) : (
            <div
              className="text-[13px] text-[hsl(var(--text-muted))]"
              data-testid="legal-terms-already"
            >
              {t("legal_terms_already_accepted")}{" "}
              <a
                href="/terms"
                target="_blank"
                rel="noreferrer"
                className="underline"
              >
                {t("legal_terms_link")}
              </a>
            </div>
          )}

          {privacyReq ? (
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={privacy}
                onChange={(e) => setPrivacy(e.target.checked)}
                className="mt-1"
                data-testid="legal-privacy-checkbox"
              />
              <span className="text-[14px] text-[hsl(var(--text))]">
                {t("legal_privacy_checkbox_prefix")}{" "}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noreferrer"
                  className="underline"
                  data-testid="legal-privacy-link"
                >
                  {t("legal_privacy_link")}
                </a>
                {t("legal_privacy_checkbox_suffix")}
              </span>
            </label>
          ) : (
            <div
              className="text-[13px] text-[hsl(var(--text-muted))]"
              data-testid="legal-privacy-already"
            >
              {t("legal_privacy_already_seen")}{" "}
              <a
                href="/privacy"
                target="_blank"
                rel="noreferrer"
                className="underline"
              >
                {t("legal_privacy_link")}
              </a>
            </div>
          )}
        </div>

        {error && (
          <div
            className="text-[13px] text-[hsl(var(--danger))] mb-3"
            data-testid="legal-error"
          >
            {error}
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button
            onClick={logout}
            className="btn btn-ghost"
            data-testid="legal-logout"
          >
            {t("legal_logout")}
          </button>
          <button
            onClick={submit}
            disabled={!canSubmit}
            className="btn btn-primary"
            data-testid="legal-submit"
          >
            {busy ? t("logging_in") : t("legal_submit")}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
